/**
 * 設定ページコンポーネント
 *
 * ユーザー設定、アカウント管理
 */

import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useT, useLanguage } from '../../i18n';

function Settings() {
  const { user, logout } = useAuth();
  const { userData, isPremium } = useUser();
  const [saving, setSaving] = useState(false);
  const { t } = useT();
  const { lang } = useLanguage();

  // 言語設定はブラウザの言語設定に依存するため、UIからは削除
  const [settings, setSettings] = useState({
    theme: userData?.settings?.theme || 'light',
    notifications: userData?.settings?.notifications ?? true
  });

  /**
   * 設定を保存
   */
  async function handleSave() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        settings: settings
      });
      alert(t('settings.settingsSaved'));
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert(t('settings.settingsSaveError'));
    } finally {
      setSaving(false);
    }
  }

  /**
   * ログアウト
   */
  async function handleLogout() {
    if (confirm(t('settings.logoutConfirm'))) {
      await logout();
    }
  }

  return (
    <div className="fade-in max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.title')}</h1>

      {/* アカウント情報 */}
      <section className="card mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('settings.account')}</h2>

        <div className="flex items-center gap-4 mb-6">
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName}
              className="w-16 h-16 rounded-full"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold">
              {user?.displayName?.charAt(0) || 'U'}
            </div>
          )}
          <div>
            <p className="font-bold text-gray-900">{user?.displayName}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
          <div>
            <p className="text-xs text-gray-500">{t('settings.plan')}</p>
            <p className="font-medium text-gray-900">
              {isPremium ? 'Premium' : 'Free'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{t('settings.registeredDate')}</p>
            <p className="font-medium text-gray-900">
              {userData?.createdAt
                ? new Date(userData.createdAt.toDate()).toLocaleDateString(lang === 'ja' ? 'ja-JP' : 'en-US')
                : t('common.unknown')}
            </p>
          </div>
        </div>
      </section>

      {/* アプリ設定 */}
      <section className="card mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('settings.appSettings')}</h2>

        {/* テーマ */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.theme')}
          </label>
          <select
            value={settings.theme}
            onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value }))}
            className="input-field"
          >
            <option value="light">{t('settings.themeLight')}</option>
            <option value="dark">{t('settings.themeDark')}</option>
          </select>
        </div>

        {/* 通知 */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{t('settings.notifications')}</p>
            <p className="text-sm text-gray-500">{t('settings.notificationsDesc')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notifications}
              onChange={(e) => setSettings(prev => ({ ...prev, notifications: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {/* 保存ボタン */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary w-full mt-6"
        >
          {saving ? t('common.saving') : t('settings.saveSettings')}
        </button>
      </section>

      {/* データ管理 */}
      <section className="card mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('settings.dataManagement')}</h2>

        {/* エクスポート（VIP以降） */}
        <div
          className="w-full p-4 rounded-xl border-2 border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-500">{t('settings.dataExport')}</p>
                <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                  {t('settings.exportBadge')}
                </span>
              </div>
              <p className="text-sm text-gray-400">
                {t('settings.dataExportDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ログアウト */}
      <section className="card">
        <button
          onClick={handleLogout}
          className="w-full py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
        >
          {t('nav.logout')}
        </button>
      </section>

      {/* バージョン情報 */}
      <p className="text-center text-xs text-gray-400 mt-6">
        KnoVelo v1.0.0
      </p>
    </div>
  );
}

export default Settings;
